import { DateTime } from '@ksyos/date-time';
import {
    Box,
    Button,
    Card,
    FormButtonRow,
    FormGroup,
    FormParagraph,
    ProcessItemContainer,
    ProcessItemHeader,
    Text,
    TextArea,
} from '@ksyos/design-system';
import { capitalize } from 'lodash';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { OutpatientClinicTask } from '@common/contexts/support/domain/models/outpatientClinicTaskModel';
import type {
    OutpatientClinicTaskNoteWithPractitionerAndWithoutTask,
    OutpatientClinicTaskWithAuthorizationAndNotes,
} from '@common/contexts/support/domain/types/customOutpatientClinicTaskTypes';
import type { IEpisodeCategoryBasic } from '@common/interfaces';
import { ToastType } from '@common/enums';
import { getLanguage } from '@common/translate/translate';
import { patientTaskNoteAddSchema, patientTaskNoteEditSchema } from '@common/validation/patientTaskNote';
import { Modal } from '@frontend/components/elements/modals/Modal';
import { TimeRangesRead } from '@frontend/components/elements/TimeRangesRead';
import { getPatientTaskDescriptionOrType } from '@frontend/utils/getPatientTaskDescriptionOrType';
import { showToast } from '@frontend/utils/showToast';
import type { IReduxState } from '../../../configureStore';
import { PatientTaskStatus } from '../../../../common/enums/patientTaskStatus';
import { StatusLabelStatus } from '../../../../common/enums/statusLabelStatus';
import { getDisplayName } from '../../../../common/utils/getDisplayName';
import { fetchJson } from '../../../store/fetch';
import { getValidationErrorTranslation } from '../../../utils/getValidationErrorTranslation';
import { emptyValidationErrors, validate } from '../../../utils/validation';
import { ConfirmationModal } from '../../elements/modals/ConfirmationModal';
import { NullableFormattedDate } from '../../elements/NullableFormattedDate';
import { PatientTaskNote } from './PatientTaskNote';
import { PatientTaskNoteHeader } from './PatientTaskNoteHeader';

interface IPatientTaskItemReadProps {
    canAddTaskNote: boolean;
    canReadTaskNote: boolean;
    episodeCategory: IEpisodeCategoryBasic;
    isCollapsed: boolean;
    onEdit: () => void;
    onSave: () => void;
    task: OutpatientClinicTaskWithAuthorizationAndNotes;
}

export function PatientTaskItemRead(props: IPatientTaskItemReadProps) {
    const [isCollapsed, setIsCollapsed] = useState(props.isCollapsed ?? false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isAddingNewNote, setIsAddingNewNote] = useState(false);
    const [newNoteText, setNewNoteText] = useState<string>('');
    const [notesWithPractitionerData, setNotesWithPractitionerData] =
        useState<OutpatientClinicTaskNoteWithPractitionerAndWithoutTask[]>();
    const [errors, setErrors] = useState(emptyValidationErrors);
    const [showEditNoteModal, setShowEditNoteModal] = useState(false);
    const [selectedNote, setSelectedNote] = useState<OutpatientClinicTaskNoteWithPractitionerAndWithoutTask>();
    const [showDeleteNoteModal, setShowDeleteNoteModal] = useState(false);
    const [isEditingOrDeletingNote, setIsEditingOrDeletingNote] = useState(false);
    const contextPractitionerId = useSelector((state: IReduxState) => state.authentication.id);
    const translate = getLanguage();

    useEffect(() => {
        getPractitionerData();
    }, [props.task.notes]);

    if (!props.task) {
        return null;
    }

    const practitioner =
        props.task.status === PatientTaskStatus.completed
            ? props.task.performerPractitioner
            : props.task.creatorPractitioner;

    const organizationName =
        props.task.status === PatientTaskStatus.completed
            ? props.task.performerOrganization?.name
            : props.task.creatorOrganization?.name;

    const canAddModifyDeleteTasks =
        !!props.task.authorization.canUpdate && props.task.status === PatientTaskStatus.open;

    const canCompleteTasks = !!props.task.authorization.canComplete && props.task.status === PatientTaskStatus.open;

    return (
        <Card>
            <Card.Transition type={isAddingNewNote ? 'edit' : 'read'} />
            <ProcessItemContainer>
                <ProcessItemHeader
                    badgeIcon={props.task.status === PatientTaskStatus.completed ? 'closed' : undefined}
                    badgeText={
                        props.task.status === PatientTaskStatus.completed
                            ? translate.StatusLabelEnum[StatusLabelStatus.completed]
                            : undefined
                    }
                    color="secondary"
                    date={getRelevantDate(props.task).toISOString()}
                    icon="todo"
                    isOpen={!isCollapsed}
                    onClick={() => setIsCollapsed(!isCollapsed)}
                    organizationName={organizationName}
                    practitionerName={practitioner ? getDisplayName(practitioner) : ''}
                    sx={isCollapsed && { marginBottom: 0 }}
                    title={getPatientTaskDescriptionOrType(props.task, props.episodeCategory)}
                />

                {!isCollapsed && (
                    <>
                        <FormGroup label={translate.contactsAndTasks.task}>
                            <FormParagraph>
                                {getPatientTaskDescriptionOrType(props.task, props.episodeCategory)}
                            </FormParagraph>
                        </FormGroup>

                        {(props.task.performerPractitioner?.id ||
                            props.task.performerPractitionerProfession ||
                            props.task.performerOrganization.id) && (
                            <FormGroup label={translate.contactsAndTasks.performer}>
                                <FormParagraph>{getPerformerLabel()}</FormParagraph>
                            </FormGroup>
                        )}

                        <FormGroup label={translate.contactsAndTasks.expectedDateCompleted}>
                            <FormParagraph>
                                <NullableFormattedDate value={props.task.expectedDateCompleted.toDate()} />
                            </FormParagraph>
                        </FormGroup>

                        <TimeRangesRead timeSlots={props.task.customControlData?.availability} />

                        {renderPatientTaskNotes()}

                        {isAddingNewNote && (
                            <FormGroup
                                hasErrors={errors.has('text')}
                                isRequired={true}
                                label={translate.contactsAndTasks.note}
                            >
                                <Box position="relative">
                                    <Box
                                        bottom={0}
                                        padding={3}
                                        position="absolute"
                                        style={{ pointerEvents: 'none' }}
                                        width="100%"
                                    >
                                        <Text
                                            fontStyle="italic"
                                            size="sm"
                                            style={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }}
                                            whiteSpace="nowrap"
                                        >
                                            {capitalize(DateTime.now().format('dddd D MMMM YYYY'))}
                                        </Text>
                                    </Box>
                                    <TextArea
                                        error={getValidationErrorTranslation(errors.get('text'))}
                                        onChange={(event) => setNewNoteText(event.target.value)}
                                        placeholder="Notitie..."
                                        required={true}
                                        style={{ display: 'block', marginBottom: 0, paddingBottom: '3rem' }}
                                        value={newNoteText}
                                    />
                                </Box>
                            </FormGroup>
                        )}

                        <ConfirmationModal
                            handleNo={() => {
                                setIsDeleting(false);
                            }}
                            handleYes={() => {
                                deletePatientTask();
                            }}
                            isOpen={isDeleting}
                            question={translate.contactsAndTasks.deleteTaskModalText}
                            title={translate.contactsAndTasks.deleteTaskModalTitle}
                        />

                        {!isAddingNewNote && props.canAddTaskNote && props.task.status === PatientTaskStatus.open && (
                            <Button
                                iconLeft="add"
                                key={'button_addNote'}
                                onClick={() => setIsAddingNewNote(true)}
                                variant="link"
                            >
                                {translate.contactsAndTasks.newNote}
                            </Button>
                        )}
                        {getTaskButtons()}
                    </>
                )}

                <ConfirmationModal
                    disabled={isEditingOrDeletingNote}
                    handleNo={() => setShowDeleteNoteModal(false)}
                    handleYes={async () => {
                        setIsEditingOrDeletingNote(true);
                        await deleteNote();
                    }}
                    isOpen={showDeleteNoteModal}
                    question={translate.contactsAndTasks.deleteNoteModalBody}
                    title={translate.contactsAndTasks.deleteNote}
                />

                <Modal
                    actionButtons={[
                        <Button
                            key="button-cancel"
                            onClick={() => {
                                setErrors(emptyValidationErrors);
                                setShowEditNoteModal(false);
                            }}
                            variant="outline"
                        >
                            {translate.common.cancel}
                        </Button>,
                        <Button
                            className="automation-button"
                            isDisabled={isEditingOrDeletingNote}
                            key="button-save"
                            onClick={async () => {
                                setIsEditingOrDeletingNote(true);
                                await editNote();
                            }}
                        >
                            {translate.common.save}
                        </Button>,
                    ]}
                    isOpen={showEditNoteModal}
                    onClose={() => setShowEditNoteModal(false)}
                    size="medium"
                    title={translate.contactsAndTasks.editNote}
                >
                    <Box mb={3}>
                        <PatientTaskNoteHeader
                            note={selectedNote!}
                            size="medium"
                        />
                    </Box>
                    <TextArea
                        error={getValidationErrorTranslation(errors.get('text'))}
                        onChange={(e) => setSelectedNote({ ...selectedNote!, text: e.target.value! })}
                        size="large"
                        value={selectedNote?.text ?? ''}
                        variant="outline"
                    />
                </Modal>
            </ProcessItemContainer>
        </Card>
    );

    async function completePatientTask() {
        const url = `/api/patientTasks/${props.task.id}/complete`;
        await fetchJson(url, 'POST');

        props.onSave();
    }

    async function deletePatientTask() {
        const url = `/api/patientTasks/${props.task.id}`;
        await fetchJson(url, 'DELETE', {});

        props.onSave();
    }

    function getTaskButtons() {
        const buttons: JSX.Element[] = [];

        if (isAddingNewNote) {
            buttons.push(
                <Button
                    key={'button-cancel'}
                    onClick={() => {
                        setIsAddingNewNote(false);
                        setNewNoteText('');
                        setErrors(emptyValidationErrors);
                    }}
                    variant="outline"
                >
                    {translate.common.cancel}
                </Button>,
                <Button
                    key={'button-save'}
                    onClick={async () => await saveNewNote()}
                    variant="solid"
                >
                    {translate.common.saveButton}
                </Button>
            );
        } else {
            if (props.task.authorization.canDelete) {
                buttons.push(
                    <Button
                        key={'button-remove'}
                        onClick={() => setIsDeleting(true)}
                        variant="outline"
                    >
                        {translate.common.remove}
                    </Button>
                );
            }

            if (canAddModifyDeleteTasks) {
                buttons.push(
                    <Button
                        key={'button-edit'}
                        onClick={() => props.onEdit()}
                        variant="outline"
                    >
                        {translate.contactsAndTasks.editTask}
                    </Button>
                );
            }
            if (canCompleteTasks) {
                buttons.push(
                    <Button
                        key={'button-complete'}
                        onClick={async () => await completePatientTask()}
                        variant="solid"
                    >
                        {translate.contactsAndTasks.completeTask}
                    </Button>
                );
            }
        }
        if (buttons.length === 0) {
            return;
        }

        return <FormButtonRow justify="right">{buttons}</FormButtonRow>;
    }

    async function getPractitionerData() {
        const newNotes: OutpatientClinicTaskNoteWithPractitionerAndWithoutTask[] = [];

        if (props.task.notes?.length) {
            for (const note of props.task.notes) {
                if (note.practitionerId) {
                    const result = await fetchJson(
                        `/api/practitioners/${note.practitionerId}/?includeProfileThumbnail=true`,
                        'GET'
                    );

                    if (!result.errorCode) {
                        newNotes.push({ ...note, practitioner: result.json });
                    }
                } else {
                    newNotes.push({ ...note, practitioner: null });
                }
            }
        }

        setNotesWithPractitionerData(newNotes);
    }

    function renderPatientTaskNotes() {
        if (notesWithPractitionerData && props.canReadTaskNote) {
            return notesWithPractitionerData.map((note, index) => {
                return (
                    <FormGroup
                        key={`note-${index}`}
                        label={index === 0 ? translate.contactsAndTasks.notes : ' '}
                    >
                        <PatientTaskNote
                            key={index}
                            note={note}
                            onDelete={() => onDeleteNoteButtonPressed(note)}
                            onEdit={() => onEditNoteButtonPressed(note)}
                            practitionerCanEditDeleteNotes={canAddModifyDeleteTasks}
                        />
                    </FormGroup>
                );
            });
        }
    }

    async function onEditNoteButtonPressed(note: OutpatientClinicTaskNoteWithPractitionerAndWithoutTask) {
        setErrors(emptyValidationErrors);
        setSelectedNote(note);
        setShowEditNoteModal(true);
    }

    async function onDeleteNoteButtonPressed(note: OutpatientClinicTaskNoteWithPractitionerAndWithoutTask) {
        setSelectedNote(note);
        setShowDeleteNoteModal(true);
    }

    async function saveNewNote() {
        const newNote = {
            practitionerId: contextPractitionerId,
            taskId: props.task.id,
            text: newNoteText,
        };

        const validationResult = validate(patientTaskNoteAddSchema, newNote);

        if (!validationResult.hasErrors) {
            try {
                await fetchJson('/api/patientTaskNotes', 'POST', validationResult.item);
                setIsAddingNewNote(false);
                setNewNoteText('');
                props.onSave();
            } catch (_) {
                showToast(ToastType.error, translate.common.generalError);
            }
        } else {
            setErrors(validationResult.errors);
        }
    }

    async function editNote() {
        const validationResult = validate(patientTaskNoteEditSchema, selectedNote);

        if (!validationResult.hasErrors) {
            try {
                await fetchJson(`/api/patientTaskNotes/${selectedNote!.id}`, 'POST', validationResult.item);
                setIsEditingOrDeletingNote(false);
                setShowEditNoteModal(false);
                props.onSave();
            } catch (_) {
                showToast(ToastType.error, translate.common.generalError);
                setIsEditingOrDeletingNote(false);
            }
        } else {
            setErrors(validationResult.errors);
            setIsEditingOrDeletingNote(false);
        }
    }

    async function deleteNote() {
        try {
            await fetchJson(`/api/patientTaskNotes/${selectedNote!.id}`, 'DELETE');
            setIsEditingOrDeletingNote(false);
            setShowDeleteNoteModal(false);
            props.onSave();
        } catch (_) {
            showToast(ToastType.error, translate.common.generalError);
            setIsEditingOrDeletingNote(false);
        }
    }

    function getPerformerLabel() {
        if (props.task.performerPractitioner?.id) {
            return getDisplayName(props.task.performerPractitioner);
        }

        if (props.task.performerPractitionerProfession) {
            return translate.professionEnum[props.task.performerPractitionerProfession];
        }

        return '-';
    }

    function getRelevantDate(task: OutpatientClinicTask) {
        return DateTime.from(
            task.status === PatientTaskStatus.completed && task.dateCompleted
                ? task.dateCompleted
                : task.expectedDateCompleted
        );
    }
}
